/* Shared "what else should this customer see" logic — used by BOTH the
   cart drawer's upsell row (assets/gw-cart-drawer.js) and the product
   page's "You may also like" section (sections/related-products.liquid),
   so the decision of which products to show only ever lives in one
   place.

   ROUND — FIXED TWO REAL BUGS found from a live report ("always shows 2
   coasters"):

   1) product.type is BLANK on this store (checked live: 0/30 sampled
      products across the catalog have it set at all). The "max N per
      type" variety rule was keying off that empty string, so every
      candidate collapsed into one shared 'unknown' bucket and the cap
      silently limited EVERY tier's results to maxPerType (2) total,
      no matter how many good candidates existed. Fixed by deriving a
      real type key from the candidate's own collection membership
      instead — the same {franchise}-{suffix} sub-collections the
      franchise pages' filter pills already use (see TYPE_SUFFIXES
      below), which this catalog actually maintains consistently.

   2) Nothing stopped a Pop Protector CASE line from being picked as the
      matching anchor. Cases carry zero tags of any kind, so every real
      tier skipped and the anchor fell straight through to the generic
      franchise/recommendations fallback — which, for a low-signal
      accessory like a case, returned whatever Shopify's own algorithm
      guessed (coasters). Cases (and anything already in a bundle deal)
      are now excluded from anchor SELECTION, not just from candidate
      RESULTS — that exclusion is done by the caller (gw-cart-drawer.js
      picks the anchor), this module just also refuses to run its own
      "franchise" fallback through the low-quality recommendations API
      unless the anchor genuinely has no franchise tag at all.

   HARD RULE: coasters, potions, or anything else carrying a
   bundle-3-for-* tag must NEVER appear as a normal suggestion here —
   those only belong in the cart drawer's separate "complete the deal"
   row (assets/gw-cart-drawer.js's own dealCandidates/
   findIncompleteBundleGroup, which this module has nothing to do
   with). Enforced once, centrally, in hydrate() below, so every tier
   and every caller gets this for free.

   COVERAGE, as of the last check against this store:
     - character:<slug> tags: exist today, but only on the 16 "I ❤️ /
       I Love [Character] - Mystery Parcel" products (one tag per
       product — dean, sam, cas [not "castiel" — that's the slug this
       store already uses], crowley, bobby, charlie, the-winchesters,
       plus several TVD ones). See catalog-audit/spn-character-tags-
       review.xlsx for the full slug list and the wider tagging plan
       under review for the rest of the catalog.
     - design:<slug> tags: none exist anywhere yet.
     - goblin.character_name / goblin.parcel_icon metafields: DO exist
       and ARE set, but only on those same 16 Mystery Parcel products
       (checked their actual values directly, not just the definition).
     - Character COLLECTIONS (e.g. handle "castiel"): exist, but very
       sparse (surveyed ~30 known character names, only "castiel"
       resolved to a real collection). Works today where it exists;
       automatically for any future one.
     - Normalised title: the practical fallback that has real coverage
       today, but can't solve deliberately-stripped aliases (e.g. "Angel
       (Halo)" for Castiel) — that needs real tag/metafield data.

   Priority per anchor product:
     a) design:<slug> tag — the tightest possible match: same artwork
        across product types (a bag + a purse + a pin, etc.)
     b) character:<slug> tag(s) — may be more than one (e.g. a
        Winchester-brothers design tagged character:dean AND
        character:sam)
     c) goblin.character_name metafield value
     d) character collection (see coverage note above)
     e) normalised title
     f) franchise — same-franchise products, ranked by Shopify's own
        best-selling sort (not arbitrary search order), preferring
        product types that complement the anchor's own type (see
        TYPE_AFFINITY) — also used to TOP UP any of a) through e) if
        they came back with fewer than the requested limit, so the
        customer-facing row still tries to show a full row rather than
        stopping short just because the best-matching tier ran out of
        real candidates.

   TAG LOOKUP METHOD (a, b, f above) — this matters, so it's spelled
   out: predictive search's `tag:` query is NOT a real tag filter on
   this store — tested live, searching tag:character:cas returned
   unrelated "canvas" shoe products (a fuzzy text match on the
   substring "cas", not a tag lookup at all). A collection tag-filter
   URL (/collections/all/<tag-handle>) IS a real Shopify feature, but
   it has its own trap: if the tag has zero matching products, Shopify
   silently falls back to rendering the unfiltered "all" collection
   instead of an empty result (also confirmed live) — so its output
   can't be trusted on its own either. The fix used here composes the
   two safely: fetch candidate handles from the tag-filtered collection
   (via the Section Rendering API, ?section_id=main-collection-
   product-grid, so the response is just the product grid, not a full
   page with header/footer/nav links mixed in — and for the franchise
   tier, with &sort_by=best-selling so the order is real, not
   incidental), then hydrate each candidate through gw-product-data
   (which this module always does anyway, for accurate price/variant/
   type) and REQUIRE the exact tag string to be present in that
   candidate's own real tags array before accepting it. That makes a
   fallback-to-"all" response yield zero real results (correct), and a
   genuine match yield only products that provably carry the exact tag
   (verified live against a known 14-product tag: 14/14 returned, 0
   false positives, 0 missed).

   DEBUG: add ?upsell_debug=1 to any page's URL to log each tier tried,
   what it returned, and why things were filtered, to the console. Off
   by default — silent in production. */
(function () {
  var DEBUG = (function () {
    try { return new URLSearchParams(window.location.search).get('upsell_debug') === '1'; }
    catch (e) { return false; }
  })();
  function log() {
    if (!DEBUG) return;
    var args = ['[upsell]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  var productDataCache = {};
  function fetchProductData(handle) {
    if (productDataCache[handle]) return productDataCache[handle];
    var p = fetch('/products/' + handle + '?section_id=gw-product-data')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var m = html.match(/<script[^>]*id="gw-product-data-json"[^>]*>([\s\S]*?)<\/script>/);
        return m ? JSON.parse(m[1]) : null;
      })
      .catch(function () { return null; });
    productDataCache[handle] = p;
    return p;
  }

  function normalizeDesignName(title) {
    var stripped = (title || '').replace(/\s*\([^)]*\)/g, '').trim();
    var name = (stripped.split(' – ')[0] || stripped).split(' - ')[0];
    return name.trim();
  }

  function tagsWithPrefix(tags, prefix) {
    return (tags || []).filter(function (t) { return t.indexOf(prefix) === 0; });
  }
  function franchiseTagOf(tags) {
    return tagsWithPrefix(tags, 'franchise:')[0];
  }
  function isBundleTagged(tags) {
    return (tags || []).some(function (t) { return t.indexOf('bundle-3-for-') === 0; });
  }

  // Subscription/mystery-box products (Dybbuk Box, HEXBOX Case File, ...)
  // are their own distinct purchase decision, never a sensible upsell for
  // something else — checked live, they don't share any tag (most have
  // none at all), so this checks the two signals that actually are
  // consistent across them: HEXBOX's real product_type is literally
  // "Mystery Box" (one of the few products on this store where that
  // field is even set), and every monthly Dybbuk Box edition lives in
  // the "dybbuk-box-1" collection (matched by prefix so future
  // dybbuk-box-* collections are covered automatically too).
  function isSubscriptionProduct(rawType, collections, hasSellingPlan) {
    if (hasSellingPlan) return true;
    var type = (rawType || '').toLowerCase();
    if (type.indexOf('mystery box') !== -1 || type.indexOf('subscription') !== -1) return true;
    return (collections || []).some(function (c) { return c.indexOf('dybbuk-box') === 0; });
  }

  // Built from a real survey of this store's manual collections — words
  // that show up on franchise-wide or product-type collections, not
  // character ones (e.g. "buffy-apparel", "supernatural-magnets").
  var GENERIC_COLLECTION_WORDS = [
    'sale', 'new-arrival', 'accessor', 'gift', 'mystery', 'clothing', 'collectible',
    'stationery', 'drinkware', 'bag', 'all', 'franchise', 'exclusive', 'wand',
    'potion', 'coaster', 'pin', 'badge', 'keychain', 'id-card', 'cut-out', 'cutout',
    'wallpaper', 'poster', 'set', 'apparel', 'plush', 'magnet', 'lanyard', 'plaque',
    'notebook', 'cloche', 'phone-grip', 'phone-background', 'fanny-pack', 'artefact',
    'legging', 'purse', 'backpack', 'footwear', 'premium-collection', 'winter',
    'digital-download', 'christmas', 't-shirt', 'hoodie', 'sweatshirt', 'funko',
    'supernatural', 'buffy', 'tvd', 'charmed', 'harry-potter', 'lord-of-the-rings',
    'labyrinth', 'dark-crystal', 'never-ending-story', 'anime', 'hocus-pocus',
    'nightmare-before-christmas', 'tim-burton', 'lost-boys', 'zelda', 'doctor-who',
    'shadowhunters', 'the-boys',
  ];
  function isCharacterLikeCollection(handle) {
    if (!handle) return false;
    var h = handle.toLowerCase();
    return !GENERIC_COLLECTION_WORDS.some(function (g) { return h.indexOf(g) !== -1; });
  }

  // Same suffix allowlist the franchise-page filter pills use
  // (sections/main-collection-type-carousel.liquid) — this is the real,
  // consistently-maintained type categorisation on this store, unlike
  // the blank product.type field. Longest-first so e.g.
  // "trousers-pants-goblin-wood" is checked before the shorter
  // "trousers-pants" it also ends with.
  var TYPE_SUFFIXES = [
    't-shirts', 'coasters', 'bags', 'blankets', 'drinkware', 'coffee-mugs', 'pin-badges',
    'keychains', 'magnets', 'metal-signs', 'plaques', 'tote-shopping-bags',
    'cross-body-handbags', 'retro-fanny-packs', 'potions', 'artefacts', 'motels',
    'pencil-cases', 'trousers-pants-goblin-wood', 'trousers-pants', 'loungewear',
    'swimwear', 'stationery', 'phone-backgrounds', 'phone-kindle-grip', 'lanyards',
    'plush', 'bucket-bags', 'gloves-goblin-wood', 'gloves', 'car-seat-covers',
    'handbags', 'headwear', 'messenger-bags', 'mystery-parcels', 'toiletry-bags',
    'footwear', 'leggings', 'purses', 'keyrings', 'mini-backpacks',
    'hoodies-sweatshirts', 'festival-bags', 'cool-bags', 'id-cards', 'glasses-cases',
    'luggage-covers', 'i-love-mystery-parcels', 'hoodies', 'notebooks',
    'multi-pocket-bags', 'phone-grips', 'cross-bifold-purses', 'funko', 'thrilljoy-pix',
    'cardboard-cutouts', 'sunglasses', 'socks', 'wands', 'cloches', 'magical-creatures', 'scarves',
  ].sort(function (a, b) { return b.length - a.length; });

  function deriveType(collections) {
    if (!collections || !collections.length) return 'unknown';
    for (var i = 0; i < collections.length; i++) {
      var h = collections[i];
      for (var j = 0; j < TYPE_SUFFIXES.length; j++) {
        var suf = TYPE_SUFFIXES[j];
        var needle = '-' + suf;
        if (h.length > needle.length && h.slice(-needle.length) === needle) return suf;
      }
    }
    return 'unknown';
  }

  // What pairs well with the anchor's own type — used to rank the
  // franchise-fallback tier so real complements (a bag's purse/pins/
  // keychains, a tee's hoodie/beanie) surface before arbitrary same-
  // franchise filler. Coasters/potions never appear here at all (the
  // hard rule strips them in hydrate() before this ever runs).
  var TYPE_AFFINITY = {
    'mini-backpacks': ['purses', 'cross-bifold-purses', 'pin-badges', 'keychains', 'keyrings', 'tote-shopping-bags'],
    'bags': ['purses', 'pin-badges', 'keychains', 'keyrings', 'tote-shopping-bags'],
    'handbags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'cross-body-handbags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'bucket-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'messenger-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'festival-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'cool-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'toiletry-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'multi-pocket-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'retro-fanny-packs': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'tote-shopping-bags': ['purses', 'pin-badges', 'keychains', 'keyrings'],
    'purses': ['mini-backpacks', 'pin-badges', 'keychains', 'keyrings', 'tote-shopping-bags'],
    'cross-bifold-purses': ['mini-backpacks', 'pin-badges', 'keychains', 'keyrings'],
    't-shirts': ['hoodies', 'hoodies-sweatshirts', 'headwear', 'pin-badges'],
    'hoodies': ['t-shirts', 'headwear', 'pin-badges'],
    'hoodies-sweatshirts': ['t-shirts', 'headwear', 'pin-badges'],
    'loungewear': ['t-shirts', 'headwear'],
    'swimwear': ['t-shirts', 'headwear'],
    'leggings': ['t-shirts', 'headwear'],
    'trousers-pants': ['t-shirts', 'headwear'],
    'trousers-pants-goblin-wood': ['t-shirts', 'headwear'],
    'footwear': ['t-shirts', 'headwear'],
    'socks': ['t-shirts', 'headwear'],
    'headwear': ['gloves', 'gloves-goblin-wood', 'scarves', 'headwear', 'bags', 'pin-badges'],
    'gloves': ['headwear', 'scarves', 'gloves-goblin-wood'],
    'gloves-goblin-wood': ['headwear', 'scarves', 'gloves'],
    'scarves': ['headwear', 'gloves', 'gloves-goblin-wood'],
    'funko': ['funko', 'keychains', 'pin-badges'],
    'thrilljoy-pix': ['funko', 'keychains', 'pin-badges'],
    'cardboard-cutouts': ['funko'],
    'drinkware': ['drinkware', 'coffee-mugs'],
    'coffee-mugs': ['drinkware', 'coffee-mugs'],
  };

  // Apparel/headwear/gloves anchors skip the character-tag-family tiers
  // entirely and go straight to franchise + TYPE_AFFINITY instead — found
  // live: the Winchester Brothers beanie carries character:dean and
  // character:sam (from the earlier bulk-tagging pass), and that tier
  // was surfacing Dean cardboard cutouts and "I ❤ Dean" mystery parcels
  // ahead of anything actually complementary to a beanie. A design: tag
  // is NOT skipped here — that's a deliberately tighter, intentional
  // same-artwork match (a design-tagged tee SHOULD surface its own
  // matching hoodie/beanie), unlike a generic character tag, which is
  // just "this exists somewhere in the same character's merch" with no
  // guarantee of a complementary type.
  var SKIP_CHARACTER_TIERS_FOR_TYPES = [
    't-shirts', 'hoodies', 'hoodies-sweatshirts', 'loungewear', 'swimwear',
    'trousers-pants', 'trousers-pants-goblin-wood', 'leggings', 'footwear',
    'socks', 'headwear', 'gloves', 'gloves-goblin-wood', 'scarves',
  ];

  // These types are too narrow/specific a "collectible" to make sense as
  // a generic upsell for an anchor of some other type — a customer
  // buying a beanie doesn't want a cardboard cutout just because it
  // shares a character tag. Still shown when the anchor itself IS that
  // type (so a cardboard-cutout product page can still recommend other
  // cutouts). Mystery parcels are excluded the same way: they're their
  // own distinct purchase decision, not a natural add-on to anything
  // else. Pop Protector cases are already excluded everywhere via the
  // separate caseHandles mechanism.
  var NEVER_UNLESS_SAME_TYPE = ['cardboard-cutouts', 'mystery-parcels', 'i-love-mystery-parcels'];

  function applyTypeAffinity(products, anchorType) {
    var prefs = TYPE_AFFINITY[anchorType];
    if (!prefs || !prefs.length) return products;
    return products
      .map(function (p, i) { return { p: p, i: i, r: (function () { var idx = prefs.indexOf(p.product_type); return idx === -1 ? prefs.length : idx; })() }; })
      .sort(function (a, b) { return a.r - b.r || a.i - b.i; })
      .map(function (x) { return x.p; });
  }

  function predictiveSearch(query, limit) {
    return fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + (limit || 16) + '&resources[options][unavailable_products]=hide')
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d.resources && d.resources.results && d.resources.results.products) || []; })
      .catch(function () { return []; });
  }

  function tagToHandle(tag) {
    return tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // See the TAG LOOKUP METHOD note at the top of this file — the
  // collection-URL response is only a candidate list; the exact-tag
  // check happens after hydration below. `sortBy` (e.g.
  // "best-selling") is only meaningful for the franchise tier — the
  // design/character tag tiers don't pass one, since relevance there
  // is "carries the tag at all", not popularity.
  function productsByTagCandidates(tag, limit, sortBy) {
    var tagHandle = tagToHandle(tag);
    var url = '/collections/all/' + tagHandle + '?section_id=main-collection-product-grid';
    if (sortBy) url += '&sort_by=' + sortBy;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var handles = [];
        var re = /\/products\/([a-z0-9-]+)/gi;
        var m;
        while ((m = re.exec(html))) {
          if (handles.indexOf(m[1]) === -1) handles.push(m[1]);
        }
        return handles.slice(0, limit || 20).map(function (h) { return { handle: h }; });
      })
      .catch(function () { return []; });
  }

  function exactTagFilter(products, tag) {
    return products.filter(function (p) { return (p.tags || []).indexOf(tag) !== -1; });
  }

  function recommendedProducts(productId, limit) {
    return fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + (limit || 12) + '&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (d) { return d.products || []; })
      .catch(function () { return []; });
  }

  /* predictive search / recommendations.json each return "price" as a
     decimal STRING in the shop's currency (e.g. "12.00"), NOT the
     integer-minor-units format /cart.js and this module's own
     gw-product-data use — and none of them reliably supply a real
     variant id at all (checked live: predictive search results came
     back with variants []). Rather than trust any of that, every raw
     candidate (whichever tier it came from) gets re-fetched through
     THIS module's own gw-product-data endpoint once it's shortlisted —
     a handful of small, cached fetches, but it's the only way every
     candidate ends up with a correct price, a real add-to-cart variant
     id, and a real derived type (see deriveType above). This is also
     where the HARD RULE lives: anything bundle-tagged (coasters,
     potions, ...) is stripped here, centrally, so no tier or caller can
     accidentally show one outside the dedicated deal-completion row —
     and where NEVER_UNLESS_SAME_TYPE is enforced (cardboard cutouts /
     mystery parcels never show as an upsell for some OTHER type of
     anchor). `anchorType` is optional — pass it whenever the caller
     knows it, so this same-type exception can apply. */
  function hydrate(rawList, excludeHandles, caseHandles, anchorType) {
    var handles = [];
    rawList.forEach(function (p) {
      if (!p || !p.handle) return;
      if (handles.indexOf(p.handle) !== -1) return;
      if (excludeHandles.indexOf(p.handle) !== -1) return;
      if (caseHandles.indexOf(p.handle) !== -1) return;
      handles.push(p.handle);
    });
    return Promise.all(handles.map(fetchProductData)).then(function (dataList) {
      var dropped = [];
      var kept = dataList.filter(function (d) {
        if (!d) return false;
        if (d.available === false) { dropped.push([d.handle, 'unavailable']); return false; }
        if (isBundleTagged(d.tags)) { dropped.push([d.handle, 'bundle-tagged (hard rule)']); return false; }
        if (isSubscriptionProduct(d.product_type, d.collections, d.has_selling_plan)) { dropped.push([d.handle, 'subscription/mystery-box product']); return false; }
        return true;
      }).map(function (d) {
        return {
          id: d.id,
          title: d.title,
          handle: d.handle,
          url: d.url,
          product_type: deriveType(d.collections),
          tags: d.tags || [],
          price: d.price,
          featured_image: d.featured_image,
          variants_count: d.variants_count,
          first_variant_id: d.first_variant_id,
          // Real cause of the "every related card looked sold out" bug:
          // the filter just above already uses d.available (from
          // sections/gw-product-data.liquid's real product.available)
          // to drop genuinely unavailable candidates, but this object
          // never carried that field forward — so every caller reading
          // p.available downstream got `undefined`, which read as
          // falsy/sold-out for ALL of them, in-stock or not. Carrying
          // it through here means related-products.liquid's own sold-
          // out check is reading the same real value gw-badge-slot/
          // gw-quick-add use elsewhere, not a second, disagreeing one
          // (it'll always be true in practice, since the filter above
          // already excludes anything false — but that's the correct,
          // single-source-of-truth state to leave this in).
          available: d.available,
        };
      }).filter(function (p) {
        if (NEVER_UNLESS_SAME_TYPE.indexOf(p.product_type) !== -1 && p.product_type !== anchorType) {
          dropped.push([p.handle, 'never-unless-same-type (' + p.product_type + ')']);
          return false;
        }
        return true;
      });
      if (DEBUG && dropped.length) log('hydrate() dropped', dropped);
      return kept;
    });
  }

  // Max N per (derived) type, in whatever order they arrived (candidate
  // lists are already relevance-ordered by the source they came from —
  // best-selling for the franchise tier, exact-tag order otherwise).
  // Also skips anything already picked in a previous tier (used when
  // topping up from the franchise fallback).
  function dedupeByType(products, maxPerType, limit, alreadyPicked) {
    var counts = {};
    (alreadyPicked || []).forEach(function (p) {
      var type = p.product_type || 'unknown';
      counts[type] = (counts[type] || 0) + 1;
    });
    var pickedHandles = (alreadyPicked || []).map(function (p) { return p.handle; });
    var out = [];
    for (var i = 0; i < products.length && out.length < limit; i++) {
      var p = products[i];
      if (pickedHandles.indexOf(p.handle) !== -1) continue;
      var type = p.product_type || 'unknown';
      counts[type] = counts[type] || 0;
      if (counts[type] >= maxPerType) continue;
      counts[type]++;
      out.push(p);
    }
    return out;
  }

  function findMatching(anchorHandle, opts) {
    opts = opts || {};
    var excludeHandles = (opts.excludeHandles || []).concat([anchorHandle]);
    var caseHandles = opts.caseHandles || [];
    var limit = opts.limit || 8;
    var maxPerType = opts.maxPerType || 2;

    return fetchProductData(anchorHandle).then(function (anchor) {
      if (!anchor) { log('anchor fetch failed for', anchorHandle); return { tier: 'none', products: [] }; }
      var anchorType = deriveType(anchor.collections);
      log('anchor:', anchorHandle, '| derived type:', anchorType, '| tags:', anchor.tags);

      function searchTier(query) {
        return predictiveSearch(query, 20).then(function (products) {
          return hydrate(products, excludeHandles, caseHandles, anchorType);
        });
      }

      function franchiseFallback() {
        var franchiseTag = franchiseTagOf(anchor.tags);
        if (!franchiseTag) {
          log('franchise tier: anchor has no franchise: tag — using generic recommendations as a last resort');
          return recommendedProducts(anchor.id, 20).then(function (products) {
            return hydrate(products, excludeHandles, caseHandles, anchorType);
          });
        }
        return productsByTagCandidates(franchiseTag, 40, 'best-selling').then(function (candidates) {
          return hydrate(candidates, excludeHandles, caseHandles, anchorType);
        }).then(function (hydrated) {
          var exact = exactTagFilter(hydrated, franchiseTag);
          var ranked = applyTypeAffinity(exact, anchorType);
          log('franchise tier (' + franchiseTag + ', best-selling):', exact.length, 'candidates after exact-tag filter, reordered by type affinity for anchor type "' + anchorType + '"');
          return ranked;
        });
      }

      // Each tier is {name, run: () => Promise<rawHydratedCandidates>},
      // tried in order until one yields at least one usable candidate.
      var designTag = tagsWithPrefix(anchor.tags, 'design:')[0];
      var characterTags = tagsWithPrefix(anchor.tags, 'character:');
      var charCollection = (anchor.collections || []).find(isCharacterLikeCollection);
      var designName = normalizeDesignName(anchor.title);

      function exactTagTier(tag) {
        return productsByTagCandidates(tag, 24).then(function (candidates) {
          return hydrate(candidates, excludeHandles, caseHandles, anchorType);
        }).then(function (hydrated) {
          return exactTagFilter(hydrated, tag);
        });
      }

      var tiers = [];
      if (designTag) {
        tiers.push({ name: 'design', run: function () { return exactTagTier(designTag); } });
      }
      if (characterTags.length) {
        tiers.push({
          name: 'character',
          run: function () {
            // Multiple character tags (e.g. a Winchester-brothers design
            // tagged both character:dean and character:sam) — look up
            // each and merge, so either character's other merch can
            // surface here.
            return Promise.all(characterTags.map(exactTagTier)).then(function (lists) {
              var merged = [];
              var seen = {};
              lists.forEach(function (list) {
                list.forEach(function (p) {
                  if (p && p.handle && !seen[p.handle]) { seen[p.handle] = true; merged.push(p); }
                });
              });
              return merged;
            });
          },
        });
      }
      if (anchor.character_name) {
        tiers.push({ name: 'character', run: function () { return searchTier(anchor.character_name); } });
      }
      if (charCollection) {
        tiers.push({
          name: 'character',
          run: function () {
            return fetch('/collections/' + charCollection + '/products.json?limit=24')
              .then(function (r) { return r.json(); })
              .then(function (d) { return hydrate(d.products || [], excludeHandles, caseHandles, anchorType); })
              .catch(function () { return []; });
          },
        });
      }
      if (designName) {
        tiers.push({
          name: 'character',
          run: function () {
            return searchTier(designName).then(function (hydrated) {
              // Predictive search is fuzzy — require the design name to
              // genuinely appear in the candidate's own title too, so a
              // loose match (shared common word) doesn't get shown as
              // if it were "the same design".
              return hydrated.filter(function (p) {
                return p.title && p.title.toLowerCase().indexOf(designName.toLowerCase()) !== -1;
              });
            });
          },
        });
      }

      // Walks the tier list in order, keeping the FIRST one that
      // produces any usable candidate at all as the "primary" tier
      // (that's what decides the heading), then always tries to top the
      // result up to `limit` using the franchise fallback afterwards —
      // never stops short just because the best-matching tier ran out
      // of real candidates.
      function tryTiers(index) {
        if (index >= tiers.length) { log('no design/character tier fired — going straight to franchise fallback'); return fillWithFranchise(null, []); }
        var tierName = tiers[index].name;
        return tiers[index].run().then(function (hydrated) {
          var deduped = dedupeByType(hydrated, maxPerType, limit, []);
          log('tier "' + tierName + '" (index ' + index + '):', hydrated.length, 'raw candidates ->', deduped.length, 'after max-' + maxPerType + '-per-type cap');
          if (deduped.length) return fillWithFranchise(tierName, deduped);
          return tryTiers(index + 1);
        });
      }

      function fillWithFranchise(primaryTierName, picked) {
        if (picked.length >= limit) return { tier: primaryTierName || 'franchise', products: picked };
        return franchiseFallback().then(function (hydrated) {
          var topUp = dedupeByType(hydrated, maxPerType, limit, picked);
          log('topping up from', picked.length, 'to', (picked.length + topUp.length), '(requested limit ' + limit + ') via franchise fallback');
          return { tier: primaryTierName || (topUp.length ? 'franchise' : 'none'), products: picked.concat(topUp) };
        });
      }

      if (SKIP_CHARACTER_TIERS_FOR_TYPES.indexOf(anchorType) !== -1) {
        var beforeCount = tiers.length;
        tiers = tiers.filter(function (t) { return t.name !== 'character'; });
        if (tiers.length !== beforeCount) {
          log('anchor type "' + anchorType + '" skips character-tag tiers (apparel/accessory rule) —', beforeCount - tiers.length, 'tier(s) removed, falling back to design tag (if any) then franchise + type affinity');
        }
      }

      return tryTiers(0);
    });
  }

  window.gwFindMatchingProducts = findMatching;
  window.gwProductMatchingHelpers = { normalizeDesignName: normalizeDesignName, fetchProductData: fetchProductData, deriveType: deriveType };
})();
